/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { API } from "../../../globals/globals";
import { useGetNodesQuery } from "../../../store/api/devices/deviceApiSlice";
import { urlParameterSelector } from "../../../store/filters/Nodes/selectors";
import { searchKeywordSelector } from "../../../store/search/selector";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { prepareDeviceMarkers, IDeviceMarker } from "./helpers";
import { IServiceDeviceData } from "../../../store/api/devices/types";
import { useStoreDispatch } from "../../../store/hooks";
import {
  setSelectedDevice,
  setSelectedDeviceAlarms,
  setSelectedDeviceAlarmCount,
  setOpenDeviceDrawer,
  setDrawerCounts,
} from "../../../store/api/devices/reducers";
import { useGetDrawerCountsMutation, useGetAlarmsByDeviceKeyMutation } from "../../../store/api/devices/deviceApiSlice";
import { useGetDeviceFullDetailMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { errorMessage } from "../../../utils/helpers";

export const useMapDisplay = () => {
  const dispatch = useStoreDispatch();
  const [markers, setMarkers] = useState<IDeviceMarker[]>([]);
  const [devicesWithoutLocation, setDevicesWithoutLocation] = useState<IServiceDeviceData[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<IServiceDeviceData | undefined>(undefined);
  const [rowClicked, setRowClicked] = useState(false);
  const [serialNumberCache, setSerialNumberCache] = useState<Map<string, string>>(new Map());

  const filters = useSelector(urlParameterSelector);
  const keyword = useSelector(searchKeywordSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();

  // Setup filters for API query to get all devices with location data
  const mapFilters = new URLSearchParams(filters);
  mapFilters.set("page", "0");
  mapFilters.set("size", "10000"); // Large number to get all devices
  mapFilters.set("search", keyword);

  const args = { filters: mapFilters.toString() };
  
  // Use getNodes query which now includes latitude/longitude in the response
  const { data, isLoading, refetch } = useGetNodesQuery(args, {
    pollingInterval: API.ENDPOINTS.NODES.pollingInterval || API.POLLING_INTERVAL,
  });

  // Refetch when filters or search changes
  useEffect(() => {
    refetch();
  }, [filters, refetch, keyword]);

  // Process data and prepare markers
  useEffect(() => {
    if (data?.data) {
      const deviceMarkers = prepareDeviceMarkers(data.data);
      setMarkers(deviceMarkers);
      
      // Filter devices without location data
      const devicesNoLocation = data.data.filter((device: IServiceDeviceData) => {
        const lat = device.latitude;
        const lng = device.longitude;
        return lat === undefined || lat === null || lng === undefined || lng === null;
      });
      setDevicesWithoutLocation(devicesNoLocation);
    }
  }, [data]);

  // Handle marker click - same pattern as NodeDisplay handleRowClicked
  const handleMarkerClick = async (marker: IDeviceMarker) => {
    const device = marker.device;

    if (device.modelName === "migration" || device.modelName === "unknown") {
      return;
    }

    // Exit fullscreen if active before opening drawer
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (err) {
        console.warn("Could not exit fullscreen:", err);
      }
    }

    // Set row clicked state and open drawer immediately with device data
    setRowClicked(true);
    setSelectedRowData(device);
    dispatch(setOpenDeviceDrawer(true));

    // Fetch additional data in parallel (same pattern as NodeDisplay)
    try {
      const args = { filters: `key=objectKey&value=${device.objectKey}&weightType=FULL`, deviceKey: device.objectKey };
      
      const apiPromises = [];
      apiPromises.push(
        getDrawerCounts({ deviceKey: device.objectKey, filters: "" })
          .unwrap()
          .then((counts) => {
            dispatch(setDrawerCounts(counts));
          })
      );
      apiPromises.push(
        getDeviceFullDetail({ filters: args.filters })
          .unwrap()
          .then((deviceDetail) => {
            dispatch(setSelectedDevice(deviceDetail.data[0]));
          })
      );
      apiPromises.push(
        getAlarmsByDeviceKey({ deviceKey: args.deviceKey, filters: "" })
          .unwrap()
          .then((alarmData) => {
            dispatch(setSelectedDeviceAlarms(alarmData.data));
            dispatch(setSelectedDeviceAlarmCount(alarmData.totalItems));
          })
      );

      await Promise.allSettled(apiPromises);
    } catch (error) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  // Fetch serial number for a device on hover if not already cached
  const fetchSerialNumberForMarker = async (marker: IDeviceMarker) => {
    // Check if already cached
    if (serialNumberCache.has(marker.objectKey)) {
      return;
    }

    try {
      const args = { filters: `key=objectKey&value=${marker.objectKey}&weightType=FULL` };
      const deviceDetail = await getDeviceFullDetail({ filters: args.filters }).unwrap();
      
      if (deviceDetail?.data?.[0]?.serialNumber) {
        const newCache = new Map(serialNumberCache);
        newCache.set(marker.objectKey, deviceDetail.data[0].serialNumber);
        setSerialNumberCache(newCache);
        
        // Update the marker with serial number
        setMarkers((prevMarkers) =>
          prevMarkers.map((m) =>
            m.objectKey === marker.objectKey
              ? { ...m, serialNumber: deviceDetail.data[0].serialNumber }
              : m
          )
        );
      }
    } catch (error) {
      console.error("Error fetching serial number:", error);
    }
  };

  return {
    permissions,
    markers,
    isLoading,
    selectedRowData,
    rowClicked,
    handleMarkerClick,
    handleCloseButtonClicked,
    fetchSerialNumberForMarker,
    devicesCount: markers.length,
    totalDevices: data?.data?.length || 0,
    devicesWithoutLocation,
  };
};
